import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, MenuItem, Select, Typography, SelectChangeEvent } from "@mui/material";

import { UserRoleEnum } from "../../../../../User/enums";

import { CandidateDetails } from "../../../../types/candidate";
import { Bucket, bucketList } from "../../../../enum/jobEnums";
import { CandidateStatusDropDownIcon } from "../../../../assets/icons/candidate-details-page-icons";
import { useChangeCandidateStatus } from "../../../../api/mutations/useChangeCandidateStatus";
import {
  DisregardCandidateModal,
  QualifyCandidateModal,
  useDisregardCandidateModal,
  useQualifyCandidateModal,
} from "../../../../components";

import CoachOverview from "./CoachOverview";
import AgentOverview from "./AgentOverview";
import ScoutOverview from "./ScoutOverview";
import PlayerOverview from "./PlayerOverview";
import CandidateFitScore from "./CandidateFitScore";
import CandidateOverview from "./CandidateOverview";

const CandidateOverviewTab = ({ candidateDetails }: { candidateDetails: CandidateDetails }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateDetailsPage.overviewTab" });
  const { t: tBucket } = useTranslation("goHire", { keyPrefix: "enum.bucket" });
  const { jobId, candidateId } = useParams<{ jobId: string; candidateId: string }>();
  const { mutate: changeStatus } = useChangeCandidateStatus();

  const { openDisregardCandidateModal, handleOpenDisregardCandidateModal, handleCloseDisregardCandidateModal } =
    useDisregardCandidateModal();
  const { openQualifyCandidateModal, handleOpenQualifyCandidateModal, handleCloseQualifyCandidateModal } =
    useQualifyCandidateModal();

  const isLocked =
    candidateDetails.candidateStatus === Bucket.QUALIFIED || candidateDetails.candidateStatus === Bucket.DISREGARDED;

  const handleStatusChange = (event: SelectChangeEvent<Bucket>) => {
    const newStatus = event.target.value as Bucket;
    if (!jobId || !candidateId || newStatus === candidateDetails.candidateStatus) return;

    if (newStatus === Bucket.QUALIFIED) {
      handleOpenQualifyCandidateModal();
    } else if (newStatus === Bucket.DISREGARDED) {
      handleOpenDisregardCandidateModal();
    } else {
      changeStatus({
        jobId,
        candidateId,
        payload: { bucket: newStatus },
      });
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.applicationStageContainer}>
        <Typography sx={styles.applicationStageTitle}>{t("applicationStage")}</Typography>
        <Select
          value={candidateDetails.candidateStatus}
          sx={styles.applicationStageSelect}
          IconComponent={CandidateStatusDropDownIcon}
          onChange={handleStatusChange}
          disabled={isLocked}
        >
          {bucketList.map((status) => (
            <MenuItem key={status} value={status}>
              {tBucket(status)}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <CandidateFitScore candidateDetails={candidateDetails} />
      <CandidatePersonaOverview candidateDetails={candidateDetails} />
      <CandidateOverview candidateDetails={candidateDetails} />

      {openQualifyCandidateModal && (
        <QualifyCandidateModal
          open={openQualifyCandidateModal}
          onClose={handleCloseQualifyCandidateModal}
          user={candidateDetails.user}
        />
      )}
      {openDisregardCandidateModal && (
        <DisregardCandidateModal
          open={openDisregardCandidateModal}
          onClose={handleCloseDisregardCandidateModal}
          user={candidateDetails.user}
        />
      )}
    </Box>
  );
};

export default CandidateOverviewTab;

const CandidatePersonaOverview = ({ candidateDetails }: { candidateDetails: CandidateDetails }) => {
  if (!candidateDetails.user.overview) return null;
  return (
    <>
      {candidateDetails.user.role === UserRoleEnum.PLAYER && <PlayerOverview candidateDetails={candidateDetails} />}
      {candidateDetails.user.role === UserRoleEnum.COACH && <CoachOverview candidateDetails={candidateDetails} />}
      {candidateDetails.user.role === UserRoleEnum.AGENT && <AgentOverview candidateDetails={candidateDetails} />}
      {candidateDetails.user.role === UserRoleEnum.SCOUT && <ScoutOverview candidateDetails={candidateDetails} />}
    </>
  );
};
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  applicationStageContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "#FFF",
  },
  applicationStageTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  applicationStageSelect: {
    "& .MuiSelect-icon": {
      right: "14px",
    },
    "& .MuiSelect-select": {
      color: "var(--Gray-700, #344054)",
      fontFamily: "Montserrat",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "20px",
    },
  },
};
